/* eslint-disable max-lines */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import deleteIcon from '../../../../Assets/images/svg/dar_delete.svg';
import { AuthContext } from '../../../../Contexts/AuthContext';
import { MODULE_NAMES } from '../../../../Constants/modules';
import { approvalStatusLabel,
  formatDate, formatDateTime, requestStatusLabel } from '../../../../Utils/staffingRequestFormatters';

const formatCellValue = (value) => (value || value === 0 ? value : '-');

const formatPersonName = (value) => {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) return '';

  return normalizedValue
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const formatPairValue = (primaryValue, secondaryValue) => ({
  primary: formatCellValue(primaryValue),
  secondary: formatCellValue(secondaryValue),
});

const APPROVAL_BADGE = {
  Approved: 'sr-badge sr-badge--approved',
  Rejected: 'sr-badge sr-badge--rejected',
  Open: 'sr-badge sr-badge--open',
};

const REQUEST_BADGE = {
  Closed: 'sr-badge sr-badge--closed',
  Open: 'sr-badge sr-badge--open',
};

const getPageNumbers = (page, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (__, idx) => idx + 1);
  }

  const pages = [];
  if (page <= 4) {
    pages.push(1, 2, 3, 4, 5, '...', totalPages);
  } else if (page >= totalPages - 3) {
    pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
  } else {
    pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
  }

  return pages;
};

const DeleteActionCell = ({ disabled, onDelete, srNumber, reason, isDeleting }) => (
  <div
    className="sr-delete-cell"
    onClick={(event) => event.stopPropagation()}
    title={disabled ? (reason || 'Delete unavailable') : `Delete ${srNumber}`}
    aria-label={disabled ? `Delete unavailable: ${reason || ''}` : `Delete ${srNumber}`}
  >
    <button
      type="button"
      className="sr-delete-btn"
      disabled={disabled}
      onClick={onDelete}
      aria-hidden={false}
      aria-disabled={disabled}
    >
      {isDeleting ? <span className="sr-delete-btn__spinner">...</span>
        : <img src={deleteIcon} alt="Delete" aria-hidden="true" className="sr-delete-btn__icon" />}
    </button>
  </div>
);

DeleteActionCell.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  srNumber: PropTypes.string.isRequired,
  reason: PropTypes.string,
  isDeleting: PropTypes.bool,
};

const StackedCell = ({ primary, secondary, centered }) => (
  <div className={`sr-stacked-cell${centered ? ' sr-stacked-cell--centered' : ''}`}>
    <span className="sr-stacked-cell__primary">{primary}</span>
    <span className="sr-stacked-cell__secondary">{secondary}</span>
  </div>
);

StackedCell.propTypes = {
  primary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  secondary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  centered: PropTypes.bool,
};

StackedCell.defaultProps = {
  centered: false,
};

const SelectionHeaderCell = ({ allSelected, disabled, onToggleAll }) => (
  <label
    className="sr-select-header"
    onClick={(event) => event.stopPropagation()}
  >
    <input
      type="checkbox"
      checked={allSelected}
      onChange={(event) => onToggleAll(event.target.checked)}
      aria-label="Select all rows"
      disabled={disabled}
    />
  </label>
);

SelectionHeaderCell.propTypes = {
  allSelected: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onToggleAll: PropTypes.func.isRequired,
};

const SelectionRowCell = ({ rowId, srNumber, checked, disabled, onToggle }) => (
  <div
    className="sr-select-cell"
    onClick={(event) => event.stopPropagation()}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onToggle(rowId, event.target.checked)}
      aria-label={`Select ${srNumber}`}
      disabled={disabled}
    />
  </div>
);

SelectionRowCell.propTypes = {
  rowId: PropTypes.number.isRequired,
  srNumber: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const RequestTableHeader = ({
  allPageSelected,
  canSelectRows,
  hasRows,
  onToggleAll,
  pinnedFields = [],
  pinnedOffsets = {},
  hasAccess,
  hasDeleteAccess,
}) => {
  const selectionStyle = pinnedFields.includes('selection')
    ? {
      position: 'sticky',
      top: 0,
      left: pinnedOffsets['selection'] || 0,
      zIndex: 6,
      boxShadow: 'none',
    }
    : {};

  const srnStyle = pinnedFields.includes('sr_number')
    ? {
      position: 'sticky',
      top: 0,
      left: hasAccess('update') ? pinnedOffsets['sr_number'] || 0 : 0,
      zIndex: 5,
    }
    : {};

  const actionStyle = { position: 'sticky', top: 0, right: 0, zIndex: 5 };

  return (
    <thead>
      <tr>
        {hasAccess('update') && (
          <th className="sr-th-selection" style={selectionStyle}>
            <SelectionHeaderCell
              allSelected={allPageSelected}
              disabled={!canSelectRows || !hasRows}
              onToggleAll={onToggleAll}
            />
          </th>
        )}
        <th className="sr-th-srn" style={srnStyle}>SRN</th>
        <th>Requested By</th>
        <th>Requested Date &amp; Time</th>
        <th>Project Name</th>
        <th>Project Vertical</th>
        <th>No. of Resources</th>
        <th>Experience</th>
        <th>Allocated Resources</th>
        <th>Allocation %</th>
        <th>Role</th>
        <th>Mandatory Skills</th>
        <th>Good to have Skills</th>
        <th>Start Date</th>
        <th>End Date</th>
        <th>Requesters Comments</th>
        <th>Approvers</th>
        <th>Approval By</th>
        <th>Approval Date</th>
        <th>Approval Status</th>
        <th>Approval Comments</th>
        <th>Request Status</th>
        <th>Request Comments</th>
        <th>Req. Status Changed Date</th>
        {hasDeleteAccess('delete') && (
          <th className="sr-th-actions" style={actionStyle}>Action</th>
        )}
      </tr>
    </thead>
  );
};

RequestTableHeader.propTypes = {
  allPageSelected: PropTypes.bool.isRequired,
  canSelectRows: PropTypes.bool.isRequired,
  hasRows: PropTypes.bool.isRequired,
  onToggleAll: PropTypes.func.isRequired,
  pinnedFields: PropTypes.arrayOf(PropTypes.string),
  pinnedOffsets: PropTypes.objectOf(PropTypes.number),
  hasAccess: PropTypes.func.isRequired,
  hasDeleteAccess: PropTypes.func.isRequired,
};

const RequestTableRow = ({
  row,
  isChecked,
  canSelectRows,
  canDeleteRow,
  getDeleteReason,
  isDeleting,
  onDeleteRow,
  onOpenApproval,
  onOpenClose,
  isOpenActionDisabled,
  isRequestCloseActionEnabled,
  onToggleRow,
  pinnedFields = [],
  pinnedOffsets = {},
  hasAccess,
  hasDeleteAccess,
}) => {
  const selectionCellStyle = pinnedFields.includes('selection')
    ? {
      position: 'sticky',
      top: 0,
      left: pinnedOffsets['selection'] || 0,
      zIndex: 4,
      boxShadow: 'none',
    }
    : {};

  const srnCellStyle = pinnedFields.includes('sr_number')
    ? {
      position: 'sticky',
      top: 0,
      left: hasAccess('update') ? pinnedOffsets['sr_number'] || 0 : 0,
      zIndex: 3,
    }
    : {};

  const actionCellStyle = { position: 'sticky', top: 0, right: 0, zIndex: 3 };

  return (
    <tr
      className={isChecked ? 'sr-row--selected' : ''}
    >
      {/* eslint-disable-next-line no-console */}
      {process.env.NODE_ENV !== 'production' && (console.log(
        'RequestTableRow debug',
        {
          requestId: row.requestId,
          canDelete: canDeleteRow(row.rawRow),
          reason: getDeleteReason ? getDeleteReason(row.rawRow) : null,
          rawRow: row.rawRow,
        },
      ), null)}
      {hasAccess('update') && (
        <td className="sr-td-selection" style={selectionCellStyle}>
          <SelectionRowCell
            rowId={row.requestId}
            srNumber={row.srNumber}
            checked={isChecked}
            disabled={!canSelectRows}
            onToggle={onToggleRow}
          />
        </td>
      )}
      <td className="sr-td-srn" style={srnCellStyle}>{row.srNumber}</td>
      <td>{row.requestedByDate.primary}</td>
      <td>{row.requestedByDate.secondary}</td>
      <td
        className={row.projectVertical.primary.length > 20 ? 'sr-td-tooltip' : ''}
        data-title={row.projectVertical.primary}>
        <span className="sr-truncate-text-inner">{row.projectVertical.primary}</span>
      </td>
      <td>{row.projectVertical.secondary}</td>
      <td >{row.resourceExp.primary}</td>
      <td >{row.resourceExp.secondary}</td>
      <td >{row.allocatedResources}</td>
      <td >{row.allocationPercent}</td>
      <td>{row.role}</td>
      <td>{formatCellValue(row.mandatorySkills)}</td>
      <td>{formatCellValue(row.goodToHaveSkills)}</td>
      <td>{row.startEndDate.primary}</td>
      <td>{row.startEndDate.secondary}</td>
      <td className={row.remarks.length > 0 ? 'sr-td-tooltip' : ''} data-title={row.remarks}>
        <span className="sr-truncate-text-inner">{formatCellValue(row.remarks)}</span>
      </td>
      <td>{formatCellValue(row.approvers)}</td>
      <td>{row.approvalByDate.primary}</td>
      <td>{row.approvalByDate.secondary}</td>
      <td>
        <div className="sr-status-cell">
          {row.approvalStatus === 'Open' ? (
            <button
              type="button"
              className="sr-badge sr-badge--open sr-badge-btn"
              disabled={isOpenActionDisabled}
              onClick={(event) => {
                event.stopPropagation();
                if (isOpenActionDisabled) return;
                // when opening from the row, auto-select Approved in modal
                onOpenApproval(row.rawRow, 1);
              }}
            >
              Open
            </button>
          ) : (
            <span className={APPROVAL_BADGE[row.approvalStatus] || 'sr-badge sr-badge--open'}>
              {row.approvalStatus}
            </span>
          )}
        </div>
      </td>
      <td className={row.approvalComments.length > 0 ? 'sr-td-tooltip' : ''} data-title={row.approvalComments}>
        <span className="sr-truncate-text-inner">{formatCellValue(row.approvalComments)}</span>
      </td>
      <td>
        <div className="sr-status-cell">
          {row.requestStatus === 'Open' ? (
            <button
              type="button"
              className="sr-badge sr-badge--open sr-badge-btn"
              disabled={!isRequestCloseActionEnabled}
              onClick={(event) => {
                event.stopPropagation();
                if (!isRequestCloseActionEnabled) return;
                // when opening from the row, auto-select Closed in modal
                onOpenClose(row.rawRow, 1);
              }}
            >
              Open
            </button>
          ) : (
            <span className={REQUEST_BADGE[row.requestStatus] || 'sr-badge sr-badge--open'}>
              {row.requestStatus}
            </span>
          )}
        </div>
      </td>
      <td className={row.requestComments.length > 0 ? 'sr-td-tooltip' : ''} data-title={row.requestComments}>
        <span className="sr-truncate-text-inner">{formatCellValue(row.requestComments)}</span>
      </td>
      <td>{formatCellValue(row.requestStatusChangedDate)}</td>
      {hasDeleteAccess('delete') && (
        <td className="sr-td-actions" style={actionCellStyle}>
          <DeleteActionCell
            disabled={!canDeleteRow(row.rawRow) || isDeleting}
            onDelete={() => onDeleteRow(row.rawRow)}
            srNumber={row.srNumber}
            reason={getDeleteReason ? getDeleteReason(row.rawRow) : ''}
            isDeleting={isDeleting}
          />
        </td>
      )}
    </tr>
  );
};

RequestTableRow.propTypes = {
  row: PropTypes.shape({
    requestId: PropTypes.number.isRequired,
    srNumber: PropTypes.string.isRequired,
    rawRow: PropTypes.shape({}).isRequired,
    requestedByDate: PropTypes.shape({
      primary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      secondary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
    projectVertical: PropTypes.shape({
      primary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      secondary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
    resourceExp: PropTypes.shape({
      primary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      secondary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
    allocatedResources: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    allocationPercent: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    mandatorySkills: PropTypes.string.isRequired,
    goodToHaveSkills: PropTypes.string.isRequired,
    startEndDate: PropTypes.shape({
      primary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      secondary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
    remarks: PropTypes.string.isRequired,
    approvers: PropTypes.string.isRequired,
    approvalByDate: PropTypes.shape({
      primary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      secondary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
    approvalStatus: PropTypes.string.isRequired,
    approvalComments: PropTypes.string.isRequired,
    requestStatus: PropTypes.string.isRequired,
    requestComments: PropTypes.string.isRequired,
    requestStatusChangedDate: PropTypes.string.isRequired,
  }).isRequired,
  isChecked: PropTypes.bool.isRequired,
  canSelectRows: PropTypes.bool.isRequired,
  canDeleteRow: PropTypes.func.isRequired,
  onDeleteRow: PropTypes.func.isRequired,
  onOpenApproval: PropTypes.func.isRequired,
  onOpenClose: PropTypes.func.isRequired,
  isOpenActionDisabled: PropTypes.bool.isRequired,
  isRequestCloseActionEnabled: PropTypes.bool.isRequired,
  onToggleRow: PropTypes.func.isRequired,
  pinnedFields: PropTypes.arrayOf(PropTypes.string),
  pinnedOffsets: PropTypes.objectOf(PropTypes.number),
  getDeleteReason: PropTypes.func,
  isDeleting: PropTypes.bool,
  hasAccess: PropTypes.func.isRequired,
  hasDeleteAccess: PropTypes.func.isRequired,
};

RequestTableRow.defaultProps = {
  getDeleteReason: null,
  isDeleting: false,
};

const RequestTablePagination = ({
  page,
  totalPages,
  totalRecords,
  showingFrom,
  showingTo,
  onPageChange,
}) => (
  <div className="sr-table-pagination">
    <span className="sr-pagination-summary">
      Showing {showingFrom}-{showingTo} of {totalRecords} records
    </span>
    <div className="sr-pagination-controls">
      <button
        type="button"
        className="sr-page-btn"
        disabled={page === 1}
        onClick={() => onPageChange(1)}
        aria-label="First page"
      >
        &#171;
      </button>
      <button
        type="button"
        className="sr-page-btn"
        disabled={page === 1}
        onClick={() => onPageChange(page - 1)}
        aria-label="Previous page"
      >
        &#8249;
      </button>
      {getPageNumbers(page, totalPages).map((pageNum, idx) =>
        pageNum === '...' ? (
          // eslint-disable-next-line react/no-array-index-key
          <span key={`ellipsis-${idx}`} className="sr-page-ellipsis">...</span>
        ) : (
          <button
            key={pageNum}
            type="button"
            className={`sr-page-btn${pageNum === page ? ' sr-page-btn--active' : ''}`}
            onClick={() => onPageChange(pageNum)}
            aria-label={`Page ${pageNum}`}
            aria-current={pageNum === page ? 'page' : undefined}
          >
            {pageNum}
          </button>
        ),
      )}
      <button
        type="button"
        className="sr-page-btn"
        disabled={page === totalPages}
        onClick={() => onPageChange(page + 1)}
        aria-label="Next page"
      >
        &#8250;
      </button>
      <button
        type="button"
        className="sr-page-btn"
        disabled={page === totalPages}
        onClick={() => onPageChange(totalPages)}
        aria-label="Last page"
      >
        &#187;
      </button>
    </div>
  </div>
);

RequestTablePagination.propTypes = {
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalRecords: PropTypes.number.isRequired,
  showingFrom: PropTypes.number.isRequired,
  showingTo: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

const RequestTable = ({
  rows,
  isLoading,
  selectedRequestIds,
  canSelectRows,
  canDeleteRow,
  getDeleteReason,
  deletingIds,
  onDeleteRow,
  onOpenApproval,
  onOpenClose,
  onToggleSelect,
  tableConfiguration,
}) => {
  const { userProfile } = useContext(AuthContext);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    setPage(1);
  }, [rows]);
  const permissions = userProfile?.permissions?.[MODULE_NAMES.DELIVERY_ADD_STAFFING_REQUEST] || [];
  const approvalPermissions = userProfile?.permissions?.[MODULE_NAMES.DELIVERY_ADD_STAFFING_REQUEST_APPROVAL] || [];

  const hasAccess = (action) => {
    return permissions.includes(action);
  };
  const hasApprovalAccess = (action) => {
    return approvalPermissions.includes(action);
  };
  const totalRecords = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
  const visibleRows = useMemo(() => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [rows, page]);

  const pagedRows = useMemo(() => visibleRows.map((row) => ({
    requestId: row.request_id,
    srNumber: row.sr_number || '',
    rawRow: row,
    requestedByDate: formatPairValue(
      formatPersonName(row.requested_by_name || row.submit_by),
      formatDateTime(row.submit_date),
    ),
    projectVertical: formatPairValue(row.project_name || '', row.vertical || row.project_vertical || ''),
    resourceExp: formatPairValue(row.resource_no ?? 0, row.experience || ''),
    allocatedResources: row.allocated_res ?? 0,
    allocationPercent: `${row.allocation_per ?? 0}%`,
    role: row.resource_type_name || '',
    mandatorySkills: row.skills || '',
    goodToHaveSkills: row.good_skills || '',
    startEndDate: formatPairValue(formatDate(row.start_date), formatDate(row.end_date)),
    remarks: row.remarks || '',
    approvers: formatPersonName(row.approvers),
    approvalByDate: formatPairValue(formatPersonName(row.approved_by), formatDate(row.approved_date)),
    approvalStatus: approvalStatusLabel(row.is_approved),
    approvalComments: row.approved_remarks || '',
    requestStatus: requestStatusLabel(row.is_close),
    requestComments: row.close_remarks || '',
    requestStatusChangedDate: formatDate(row.close_date),
  })), [visibleRows]);
  const showingFrom = totalRecords === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(page * PAGE_SIZE, totalRecords);

  const allPageSelected = pagedRows.length > 0
    && pagedRows.every((row) => selectedRequestIds.includes(row.requestId));

  const handleToggleAll = (selectAll) => {
    if (!canSelectRows) return;
    rows.forEach((row) => {
      const isSelected = selectedRequestIds.includes(row.request_id);
      if (selectAll && !isSelected) onToggleSelect(row.request_id);
      if (!selectAll && isSelected) onToggleSelect(row.request_id);
    });
  };

  const handleToggleRow = (rowId) => {
    if (!canSelectRows) return;
    onToggleSelect(rowId);
  };

  const isOpenActionDisabled = selectedRequestIds.length > 0;
  const isRequestCloseActionEnabled = selectedRequestIds.length === 0;


  const pinnedFields = useMemo(() => {
    if (!Array.isArray(tableConfiguration)) return [];
    return tableConfiguration
      .filter((col) => Boolean(col?.defaultPinned))
      .map((col) => col.field);
  }, [tableConfiguration]);

  const pinnedOffsets = useMemo(() => {
    const WIDTH_MAP = { selection: 56, sr_number: 104 };
    let offset = 0;
    const map = {};
    (pinnedFields || []).forEach((field) => {
      map[field] = offset;
      offset += WIDTH_MAP[field] || 120;
    });
    return map;
  }, [pinnedFields]);

  if (isLoading) {
    return (
      <div className="sr-table-container">
        <div className="sr-native-table-wrap sr-native-table-wrap--loading" aria-busy="true" aria-live="polite">
          <div className="sr-table-skeleton" aria-hidden="true">
            <div className="sr-table-skeleton__header" />
            {Array.from({ length: 10 }).map((rowItem, rowIndex) => (
              <div key={`sr-table-skeleton-row-${rowIndex + 1}`} className="sr-table-skeleton__row">
                <span className="sr-table-skeleton__line sr-table-skeleton__line--long" />
                <span className="sr-table-skeleton__line" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sr-table-container">
      <div className="sr-native-table-wrap">
        <table className="sr-table">
          <RequestTableHeader
            allPageSelected={allPageSelected}
            canSelectRows={canSelectRows}
            hasRows={pagedRows.length > 0}
            onToggleAll={handleToggleAll}
            pinnedFields={pinnedFields}
            pinnedOffsets={pinnedOffsets}
            hasAccess={hasApprovalAccess}
            hasDeleteAccess={hasAccess}
          />
          <tbody>
            {pagedRows.map((row) => {
              const isChecked = selectedRequestIds.includes(row.requestId);
              const isDeleting = Array.isArray(deletingIds) && deletingIds.includes(Number(row.requestId));
              return (
                <RequestTableRow
                  key={row.requestId}
                  row={row}
                  isChecked={isChecked}
                  canSelectRows={canSelectRows}
                  canDeleteRow={canDeleteRow}
                  onDeleteRow={onDeleteRow}
                  getDeleteReason={getDeleteReason}
                  isDeleting={isDeleting}
                  onOpenApproval={onOpenApproval}
                  onOpenClose={onOpenClose}
                  isOpenActionDisabled={isOpenActionDisabled}
                  isRequestCloseActionEnabled={isRequestCloseActionEnabled}
                  onToggleRow={handleToggleRow}
                  tableConfiguration={tableConfiguration}
                  pinnedFields={pinnedFields}
                  pinnedOffsets={pinnedOffsets}
                  hasAccess={hasApprovalAccess}
                  hasDeleteAccess={hasAccess}
                />
              );
            })}
            {pagedRows.length === 0 && (
              <tr>
                <td colSpan={24} className="sr-no-data">No records found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <RequestTablePagination
        page={page}
        totalPages={totalPages}
        totalRecords={totalRecords}
        showingFrom={showingFrom}
        showingTo={showingTo}
        onPageChange={setPage}
      />
    </div>
  );
};

RequestTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    request_id: PropTypes.number,
    is_approved: PropTypes.number,
    is_close: PropTypes.number,
    sr_number: PropTypes.string,
    submit_by: PropTypes.string,
    requested_by_name: PropTypes.string,
    submit_date: PropTypes.string,
    project_name: PropTypes.string,
    project_vertical: PropTypes.string,
    resource_no: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    experience: PropTypes.string,
    allocated_res: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    allocation_per: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    resource_type_name: PropTypes.string,
    skills: PropTypes.string,
    good_skills: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    remarks: PropTypes.string,
    approvers: PropTypes.string,
    approved_by: PropTypes.string,
    approved_date: PropTypes.string,
    approved_remarks: PropTypes.string,
    close_date: PropTypes.string,
    close_remarks: PropTypes.string,
  })).isRequired,
  isLoading: PropTypes.bool,
  selectedRequestIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  canSelectRows: PropTypes.bool.isRequired,
  canDeleteRow: PropTypes.func.isRequired,
  getDeleteReason: PropTypes.func,
  deletingIds: PropTypes.arrayOf(PropTypes.number),
  onDeleteRow: PropTypes.func.isRequired,
  onOpenApproval: PropTypes.func.isRequired,
  onOpenClose: PropTypes.func.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  tableConfiguration: PropTypes.arrayOf(PropTypes.shape({})),
};

RequestTable.defaultProps = {
  isLoading: false,
  getDeleteReason: null,
  deletingIds: [],
  tableConfiguration: [],
};

export default RequestTable;
